import { updateIntl } from "react-intl-redux"

// Translation messages
import localeFr from "./fr.json"
import localeEn from "./en.json"

// Locale code to use when a requested locale is unavailable
export const FALLBACK_LOCALE_CODE = "en"

// LocalStorage API Key for caching the user's preferred locale code
export const LOCALSTORAGE_LOCALE_CODE_KEY = "webportalLocale"

// An object containing the supported locales.
// Object key: A string, the supported locale code
// Object message: An object containing the messages of the locale code
export const supportedLocales = {
  en: localeEn,
  fr: localeFr
}

/**
 * Caches the locale code (string) into the browser.
 * To be called only when the localeString is determined to be supported and valid.
 * @param {String} localeString - locale value to cache; must be supported and valid
 */
export function setCachedLocaleCode(localeString) {
  localStorage.setItem(LOCALSTORAGE_LOCALE_CODE_KEY, localeString)
}

/**
 * Returns the cached locale code string if exists.
 * Returns undefined if nothing is cached.
 * @returns {String | undefined} - the cached locale if exists
 */
export function getCachedLocaleCode() {
  return localStorage.getItem(LOCALSTORAGE_LOCALE_CODE_KEY)
}

/**
 * Removes the country part of a locale string and returns the result
 * @param {String} localeString
 * @returns {String} truncated string
 */
export function truncateLocaleCountry(localeString) {
  return localeString.split(/[-_]/)[0]
}

/**
 * Determines whether or not a locale is supported
 * @param {String} localeString
 */
export function localeIsSupported(localeString) {
  // This locale (language AND country) is supported
  const isFullySupported = localeString in supportedLocales

  // This language is supported
  const isPartiallySupported =
    truncateLocaleCountry(localeString) in supportedLocales

  return isFullySupported || isPartiallySupported
}

/**
 * Returns the nearest supported locale code. Attempts to find full locale support
 * (language and country) if possible, else attempts to find just the language support,
 * else returns the fallback.
 * @param {String} localeString
 * @returns {String} supported locale code
 */
export function getNearestSupportedLocaleCode(localeString) {
  if (localeString in supportedLocales) {
    return localeString
  }

  const languageCode = truncateLocaleCountry(localeString)
  return languageCode in supportedLocales ? languageCode : FALLBACK_LOCALE_CODE
}

/**
 * Returns the first supported user-preferred locale, if exists.
 * Returns undefined if none of the languages are supported.
 * @returns {string | undefined}
 */
export function getFirstSupportedBrowserLocaleCode() {
  const preferredLanguages = navigator.languages ?? [navigator.language]
  return preferredLanguages.find(localeIsSupported)
}

export function updateLocale(localeString) {
  return (dispatch, getState) => {
    const state = getState()
    const { locale: currentLocale } = state.intl
    const targetLocale = getNearestSupportedLocaleCode(localeString)

    if (currentLocale === targetLocale) {
      // No changes required
      return
    }

    setCachedLocaleCode(targetLocale)

    dispatch(
      updateIntl({
        locale: targetLocale,
        messages: supportedLocales[targetLocale]
      })
    )
  }
}
